import type { PropertyFilters } from '~/core/entities/property/filters';

import { toFiniteCoordinates } from '~/core/entities/property/coordinates';
import { DEFAULT_LOCATION_SLUG } from '~/core/entities/property/filterPolicy';

// keep: same framing, clusters follow the filters; clusters-only: same framing, clusters
// reloaded for a wider area; refit: frame the new place; selection: the map already shows it.
export type MapUpdate = 'keep' | 'clusters-only' | 'refit' | 'selection';
export interface SearchTransition {
	filters: PropertyFilters;
	map: MapUpdate;
}
export interface MapViewport {
	bounds?: string;
	zoom?: number;
}

// Type, rooms, price, area, sort: the result set changes but the camera stays.
export function nextFiltersForFilterChange(
	current: PropertyFilters,
	next: PropertyFilters
): SearchTransition {
	return {
		filters: { ...next, bounds: current.bounds, zoom: current.zoom },
		map: 'keep'
	};
}

const RESET_FRAMING = { bounds: undefined, zoom: undefined, h3Ids: undefined };

// A place picked in the bar: the slug still names the previous place, so it goes.
export function nextFiltersForLocation(
	next: PropertyFilters
): SearchTransition {
	return {
		filters: { ...next, ...RESET_FRAMING, locationSlug: undefined },
		map: 'refit'
	};
}

// A link (breadcrumb, related search) names its destination, slug included.
// The macroregion of the previous search leaks in; keep only one the link names.
export function nextFiltersForLink(
	next: PropertyFilters,
	macroregion?: string
): SearchTransition {
	return {
		filters: {
			...next,
			...RESET_FRAMING,
			macroregions: macroregion ? [macroregion] : []
		},
		map: 'refit'
	};
}

// "Toda a cidade" widens to the city and keeps what the person is looking at.
export function nextFiltersForSeeAllCity(
	current: PropertyFilters,
	input: { city?: string; state?: string; viewport?: MapViewport }
): SearchTransition {
	const cities = current.cities?.length ? current.cities : listOf(input.city);
	const states = current.states?.length ? current.states : listOf(input.state);
	const viewport =
		input.viewport?.bounds && input.viewport.zoom ? input.viewport : {};
	return {
		filters: {
			...current,
			cities,
			states,
			regions: [],
			addressSlug: '',
			poiLocationLat: undefined,
			poiLocationLng: undefined,
			locationSlug: undefined,
			h3Ids: [],
			...viewport
		},
		map: 'clusters-only'
	};
}

// Picking areas narrows the list only; clearing them pins the bounds the person sees.
export function nextFiltersForAreaSelection(
	base: PropertyFilters,
	h3Ids: string[],
	viewport: MapViewport
): SearchTransition {
	const bounds = h3Ids.length ? base.bounds : (viewport.bounds ?? base.bounds);
	return {
		filters: { ...base, h3Ids, bounds, zoom: viewport.zoom },
		map: 'selection'
	};
}

export function nextFiltersForClear(
	current: PropertyFilters
): SearchTransition {
	return {
		filters: {
			transactionType: current.transactionType,
			locationSlug: DEFAULT_LOCATION_SLUG
		},
		map: 'refit'
	};
}

function listOf(value?: string): string[] {
	return value ? [value] : [];
}

// A pan or zoom by the person: the map computed the viewport, the camera stays put.
export function nextFiltersForMapMove(next: PropertyFilters): SearchTransition {
	return { filters: next, map: 'keep' };
}

// A zoom across the selection threshold drops the areas but keeps the camera.
export function nextFiltersForAreaClear(
	next: PropertyFilters
): SearchTransition {
	return { filters: { ...next, h3Ids: [] }, map: 'keep' };
}

export interface PlaceSelection {
	state: string;
	cities?: string[];
	regions?: string[];
	macroregions?: string[];
	text?: string;
	lat?: number;
	lng?: number;
}

const RESET_PLACE = {
	locationSlug: undefined,
	bounds: undefined,
	zoom: undefined,
	h3Ids: undefined,
	commercialIds: undefined,
	pluscodesGroup: undefined,
	searchTerm: undefined
};

// A suggest pick replaces the place; a point (address/POI) also names the address.
export function nextFiltersForPlace(
	current: PropertyFilters,
	place: PlaceSelection
): SearchTransition {
	const point = toFiniteCoordinates(place.lat, place.lng);
	return {
		filters: {
			...current,
			...RESET_PLACE,
			states: [place.state.toUpperCase()],
			cities: place.cities,
			regions: place.regions,
			macroregions: place.macroregions,
			addressSlug: point ? place.text : undefined,
			poiLocationLat: point?.lat,
			poiLocationLng: point?.lng
		},
		map: 'refit'
	};
}

export function nextFiltersForMacroregion(
	current: PropertyFilters,
	macroregion?: string
): SearchTransition {
	return {
		filters: {
			...current,
			macroregions: macroregion ? [macroregion] : undefined
		},
		map: 'refit'
	};
}
